import * as React from 'react'
import styled from 'styled-typed'
import { Route, Switch } from 'react-router-dom'
import { bindActionCreators, Dispatch } from 'redux'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { withRouter } from 'react-router-dom'
import { State } from 'store'
import { Account } from 'auth/state'
import { changeActiveAccount } from 'preferences/actions.async'
import { parseSearch, renderSearch } from 'navigation'
import { ResponsiveImage } from 'uiComponents/image'
import conviousPinkLogo from 'images/convious-logo-pink.png'
import { Combobox } from 'uiComponents/combobox/combobox'

const AccountSelectorWrapper = styled.div`
    @media (max-width: ${(props) => props.theme.breakPoints.md}) {
        > .convious-logo {
            display: none !important;
        }
    }
`

interface SelectorDropdownProps {
    activeAccount: string
    accounts: Account[]
    changeActiveAccount: typeof changeActiveAccount
    selfSignUp: string | null
}

interface SelectorDropdownState {
    loading: boolean
    search: string
}

class SelectorDropdown extends React.Component<
    SelectorDropdownProps & RouteComponentProps<any>,
    SelectorDropdownState
> {
    popup: HTMLDivElement | null = null

    constructor(props: SelectorDropdownProps & RouteComponentProps<any>) {
        super(props)
        this.state = {
            loading: false,
            search: '',
        }
    }

    componentDidMount() {
        const urlSlug = this.props.match.params['accountSlug']
        if (urlSlug && urlSlug !== this.props.activeAccount && this.props.accounts.find((x) => x.slug === urlSlug)) {
            this.props.changeActiveAccount(urlSlug)
        }
    }

    componentDidUpdate(prevProps: SelectorDropdownProps & RouteComponentProps<any>) {
        const oldUrlSlug = prevProps.match.params['accountSlug']
        const newUrlsSlug = this.props.match.params['accountSlug']
        if (
            oldUrlSlug !== newUrlsSlug &&
            newUrlsSlug !== this.props.activeAccount &&
            this.props.accounts.find((x) => x.slug === newUrlsSlug)
        ) {
            this.props.changeActiveAccount(newUrlsSlug)
        }
    }

    setPopupRef = (node: HTMLDivElement) => {
        this.popup = node
    }

    onChange = async (slug: string) => {
        if (this.props.activeAccount === slug && this.props.match.params['accountSlug'] === slug) {
            return
        }
        this.setState({ loading: true })
        await this.props.changeActiveAccount(slug)
        this.setState({ loading: false })
        const path = this.props.match.params[1]
        const search = this.props.location.search
        const queryItems = parseSearch(search)
        if (queryItems.page) {
            queryItems.page = '1'
        }
        const nonPersistentQueryItems = ['q', 'search', 'filter', 'checkout_funnel_rates', 'sankeyFilter']
        nonPersistentQueryItems.forEach((item) => {
            if (queryItems[item]) {
                delete queryItems[item]
            }
        })
        const query = renderSearch(queryItems)
        if (this.props.match.path.indexOf(':accountSlug') > -1) {
            this.props.history.push(`/account/${slug}/${path}${query}`)
        } else {
            this.props.history.push(`${this.props.location.pathname}${query}`)
        }
    }

    render() {
        const { accounts, activeAccount } = this.props
        const inaccessibleAccount = this.props.match.params['accountSlug']
            ? this.props.match.params['accountSlug'] !== activeAccount
            : false
        const sortedAccounts = accounts
            .map((x) => ({ label: x.name, value: x.slug }))
            .slice()
            .sort((a, b) =>
                a.label.toUpperCase() > b.label.toUpperCase()
                    ? 1
                    : b.label.toUpperCase() > a.label.toUpperCase()
                    ? -1
                    : 0,
            )

        return (
            <AccountSelectorWrapper className="safari-wrapper">
                <ResponsiveImage
                    className="convious-logo"
                    style={{ display: 'block' }}
                    src={conviousPinkLogo}
                    alt="Convious"
                />
                {accounts.length === 1 && !inaccessibleAccount ? (
                    <div className="account-name">{accounts[0].name}</div>
                ) : (
                    <Combobox
                        options={sortedAccounts}
                        value={activeAccount}
                        onChange={this.onChange}
                        disableClearable
                        disablePortal
                        multilineInput
                        loading={this.state.loading}
                    />
                )}
            </AccountSelectorWrapper>
        )
    }
}

function mapStateToProps(state: State) {
    return {
        accounts: state.auth.user ? state.auth.user.accounts : [],
        activeAccount: state.preferences.activeAccount,
    }
}

function mapDispatchToProps(dispatch: Dispatch) {
    return bindActionCreators(
        {
            changeActiveAccount,
        },
        dispatch,
    )
}

const ConnectedSelectorDropdown = withRouter(connect(mapStateToProps, mapDispatchToProps)(SelectorDropdown))

export function AccountSelector(props: { selfSignUp: string | null }) {
    return (
        <Switch>
            <Route
                path="/(account|widget)/:accountSlug/*"
                render={() => <ConnectedSelectorDropdown selfSignUp={props.selfSignUp} />}
            />
            <Route render={() => <ConnectedSelectorDropdown selfSignUp={props.selfSignUp} />} />
        </Switch>
    )
}
